import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, Package, PackageCheck, User, FileText, Mail, Euro, CheckCircle, RefreshCw, Eye, Send, ExternalLink, Pencil, X, CalendarDays, Trash2, Shield, Building2 } from 'lucide-react';
import { PackName } from '../../ui/PackName';

const API = window.location.origin + '/api';

const BLOCKED_EDIT_STATUSES = ['shipped', 'delivered', 'active', 'completed'];

const ManualOrdersView = ({ packOptions = [] }) => {
    const [step, setStep] = useState(1); // 1: Client Info, 2: Pack Selection, 3: Contract, 4: Review & Send
    const [manualOrders, setManualOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [creating, setCreating] = useState(false);
    const [showNewOrder, setShowNewOrder] = useState(false);
    const [sendingLink, setSendingLink] = useState(false);

    // Edit modal state
    const [editingOrder, setEditingOrder] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);

    // Delete modal state
    const [deletingOrder, setDeletingOrder] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Status editor modal state
    const [statusEditMO, setStatusEditMO] = useState(null);
    const [moNewStatus, setMoNewStatus] = useState('');
    const [moStatusSaving, setMoStatusSaving] = useState(false);

    const openMOStatusEditor = (order) => {
      setStatusEditMO(order);
      setMoNewStatus(order.status || 'draft');
    };

    const saveMOStatus = async () => {
      if (!statusEditMO) return;
      setMoStatusSaving(true);
      try {
        await axios.put(
          `${API}/admin/manual-orders/${statusEditMO.id}/status`,
          { status: moNewStatus },
          { withCredentials: true }
        );
        toast.success('Statut mis à jour');
        setStatusEditMO(null);
        fetchManualOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      } finally {
        setMoStatusSaving(false);
      }
    };
    // Form state
    const [orderData, setOrderData] = useState({
      seller_name: '',
      client_email: '',
      client_first_name: '',
      client_last_name: '',
      client_phone: '',
      client_company: '',
      client_address: '',
      client_postal_code: '',
      client_city: '',
      customer_type: 'client',
      selected_pack_id: '',
      // Pack custom (utilisé quand selected_pack_id est vide)
      custom_pack_name: '',
      custom_pack_surface: '',
      custom_pack_price: '',
      rental_start_date: '',
      notes: '',
      discount_type: 'percentage', // 'percentage' or 'fixed'
      discount_value: 0,
      discount_reason: ''
    });

    // Fetch manual orders
    useEffect(() => {
      fetchManualOrders();
    }, []);

    const fetchManualOrders = async () => {
      setLoadingOrders(true);
      try {
        const response = await axios.get(`${API}/admin/manual-orders`, { withCredentials: true });
        setManualOrders(response.data || []);
      } catch (error) {
        console.error('Error fetching manual orders:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    const handleInputChange = (field, value) => {
      setOrderData(prev => ({ ...prev, [field]: value }));
    };

    const validateStep = () => {
      if (step === 1) {
        if (!orderData.client_email || !orderData.client_first_name || !orderData.client_last_name) {
          toast.error('Veuillez remplir les informations obligatoires du client');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderData.client_email)) {
          toast.error('Email invalide');
          return false;
        }
      }
      if (step === 2) {
        const hasCatalogPack = !!orderData.selected_pack_id;
        const hasCustomPack = orderData.custom_pack_name && orderData.custom_pack_price;
        if (!hasCatalogPack && !hasCustomPack) {
          toast.error('Sélectionnez un pack du catalogue OU saisissez un pack personnalisé (nom + prix)');
          return false;
        }
        if (!orderData.rental_start_date) {
          toast.error('Veuillez sélectionner une date de début');
          return false;
        }
      }
      return true;
    };

    const nextStep = () => {
      if (validateStep()) {
        setStep(prev => Math.min(prev + 1, 4));
      }
    };

    const prevStep = () => {
      setStep(prev => Math.max(prev - 1, 1));
    };

    const createManualOrder = async () => {
      setCreating(true);
      try {
        // Build payload : if no catalogue pack selected, map custom fields
        const payload = { ...orderData };
        if (!orderData.selected_pack_id && orderData.custom_pack_name) {
          payload.pack_name = orderData.custom_pack_name;
          payload.pack_surface = orderData.custom_pack_surface;
          payload.pack_price = parseFloat(orderData.custom_pack_price) || 0;
          payload.monthly_price = parseFloat(orderData.custom_pack_price) || 0;
        }
        const response = await axios.post(`${API}/admin/manual-orders`, payload, {
          withCredentials: true
        });
        toast.success('Commande créée avec succès !');
        setShowNewOrder(false);
        setStep(1);
        setOrderData({
          seller_name: '',
          client_email: '',
          client_first_name: '',
          client_last_name: '',
          client_phone: '',
          client_company: '',
          client_address: '',
          client_postal_code: '',
          client_city: '',
          selected_pack_id: '',
          custom_pack_name: '',
          custom_pack_surface: '',
          custom_pack_price: '',
          rental_start_date: '',
          notes: '',
          discount_type: 'percentage',
          discount_value: 0,
          discount_reason: ''
        });
        fetchManualOrders();
        return response.data;
      } catch (error) {
        console.error('Error creating manual order:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        return null;
      } finally {
        setCreating(false);
      }
    };

    const sendReservationLink = async (orderId) => {
      setSendingLink(true);
      try {
        const response = await axios.post(`${API}/admin/manual-orders/${orderId}/send-reservation-link`, {}, {
          withCredentials: true
        });
        toast.success('Lien de réservation envoyé au client par email !');
        fetchManualOrders();
      } catch (error) {
        console.error('Error sending reservation link:', error);
        toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du lien");
      } finally {
        setSendingLink(false);
      }
    };

    const requestSEPAMandate = async (orderId) => {
      if (!window.confirm(
        "Envoyer une demande de prélèvement SEPA au client ?\n\n"
        + "Un email lui sera envoyé avec un lien direct pour signer son mandat (IBAN + signature électronique).\n"
        + "La demande apparaîtra dans 'Demandes SEPA' dès qu'il aura signé."
      )) return;
      setSendingLink(true);
      try {
        const response = await axios.post(`${API}/admin/manual-orders/${orderId}/request-sepa-mandate`, {}, {
          withCredentials: true,
        });
        toast.success(`Demande SEPA envoyée. Premier prélèvement : ${response.data.first_charge_amount.toFixed(2)} €.`);
        fetchManualOrders();
      } catch (error) {
        console.error('Error requesting SEPA mandate:', error);
        toast.error(error.response?.data?.detail || "Erreur lors de l'envoi de la demande SEPA");
      } finally {
        setSendingLink(false);
      }
    };

    const createReservationFromOrder = async (orderId) => {
      if (!window.confirm(
        "Créer la commande à préparer pour cette commande payée par virement ?\n\n"
        + "La commande apparaîtra dans 'Commandes → En cours' au statut Payé, prête à être traitée (préparation / expédition).\n"
        + "Aucun prélèvement automatique ne sera créé (virement ponctuel)."
      )) return;
      setSendingLink(true);
      try {
        const response = await axios.post(`${API}/admin/manual-orders/${orderId}/create-reservation`, {}, {
          withCredentials: true,
        });
        toast.success(`Commande à préparer créée (${response.data.order_number}). Retrouvez-la dans "Commandes → En cours".`);
        fetchManualOrders();
      } catch (error) {
        console.error('Error creating reservation from manual order:', error);
        toast.error(error.response?.data?.detail || "Erreur lors de la création de la commande à préparer");
      } finally {
        setSendingLink(false);
      }
    };


    const previewContract = async (orderId) => {
      try {
        const response = await axios.get(`${API}/admin/manual-orders/${orderId}/contract-pdf`, {
          withCredentials: true,
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error previewing contract:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la prévisualisation du contrat');
      }
    };

    const openEditModal = (order) => {
      setEditingOrder(order);
      setEditData({
        client_first_name: order.client_first_name || order.client?.firstName || '',
        client_last_name: order.client_last_name || order.client?.lastName || '',
        client_email: order.client_email || order.client?.email || '',
        client_phone: order.client_phone || order.client?.phone || '',
        client_company: order.client_company || order.client?.company || '',
        client_address: order.client_address || order.client?.address || '',
        client_postal_code: order.client_postal_code || order.client?.postalCode || '',
        client_city: order.client_city || order.client?.city || '',
        seller_name: order.seller_name || order.commercial_name || '',
        selected_pack_id: order.pack_id || order.selected_pack_id || '',
        rental_start_date: order.rental_start_date || '',
        notes: order.notes || '',
        discount_type: order.discount_type || 'percentage',
        discount_value: order.discount_value || 0,
        discount_reason: order.discount_reason || ''
      });
    };

    const handleEditChange = (field, value) => {
      setEditData(prev => ({ ...prev, [field]: value }));
    };

    const saveEdit = async () => {
      if (!editingOrder) return;
      if (!editData.client_email || !editData.client_first_name || !editData.client_last_name) {
        toast.error('Prénom, nom et email sont obligatoires');
        return;
      }
      setSaving(true);
      try {
        await axios.put(`${API}/admin/manual-orders/${editingOrder.id}`, editData, {
          withCredentials: true
        });
        toast.success('Commande modifiée avec succès !');
        setEditingOrder(null);
        fetchManualOrders();
      } catch (error) {
        console.error('Error updating manual order:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
      } finally {
        setSaving(false);
      }
    };

    const resendLink = async (orderId) => {
      setSendingLink(true);
      try {
        await axios.post(`${API}/admin/manual-orders/${orderId}/send-reservation-link`, {}, {
          withCredentials: true
        });
        toast.success('Lien de réservation renvoyé au client !');
        fetchManualOrders();
      } catch (error) {
        console.error('Error resending link:', error);
        toast.error(error.response?.data?.detail || "Erreur lors du renvoi du lien");
      } finally {
        setSendingLink(false);
      }
    };

    const editSelectedPack = packOptions.find(p => p.id === editData.selected_pack_id);
    const calculateEditDiscountedPrice = () => {
      if (!editSelectedPack) return { originalPrice: 0, discountedPrice: 0, discountAmount: 0 };
      const originalPrice = parseFloat(editSelectedPack.price) || 0;
      let discountAmount = 0;
      if (editData.discount_value > 0) {
        if (editData.discount_type === 'percentage') {
          discountAmount = originalPrice * (parseFloat(editData.discount_value) / 100);
        } else {
          discountAmount = parseFloat(editData.discount_value);
        }
      }
      return {
        originalPrice,
        discountedPrice: Math.round(Math.max(0, originalPrice - discountAmount) * 100) / 100,
        discountAmount: Math.round(discountAmount * 100) / 100
      };
    };
    const editPriceInfo = calculateEditDiscountedPrice();

    const confirmDelete = async () => {
      if (!deletingOrder || !deletePassword) return;
      setDeleting(true);
      try {
        await axios.delete(`${API}/admin/manual-orders/${deletingOrder.id}`, {
          data: { confirm_password: deletePassword },
          withCredentials: true
        });
        toast.success('Commande supprimée');
        setDeletingOrder(null);
        setDeletePassword('');
        fetchManualOrders();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      } finally {
        setDeleting(false);
      }
    };

    // Resolved active pack — catalogue OR custom (synthetic)
    const customPack = (!orderData.selected_pack_id && orderData.custom_pack_name && orderData.custom_pack_price)
      ? {
          id: 'custom',
          name: orderData.custom_pack_name,
          surface: orderData.custom_pack_surface || '',
          price: parseFloat(orderData.custom_pack_price) || 0,
          deposit: parseFloat(orderData.custom_pack_price) || 0,
          description: 'Pack personnalisé',
          isCustom: true,
        }
      : null;
    const selectedPack = packOptions.find(p => p.id === orderData.selected_pack_id) || customPack;

    const getStatusBadge = (status) => {
      const styles = {
        draft: 'bg-slate-500/20 text-slate-400',
        link_sent: 'bg-blue-500/20 text-blue-400',
        payment_sent: 'bg-yellow-500/20 text-yellow-400',
        paid: 'bg-green-500/20 text-green-400',
        cancelled: 'bg-red-500/20 text-red-400'
      };
      const labels = {
        draft: 'Brouillon',
        link_sent: 'Lien envoyé',
        contract_sent: 'Lien envoyé',
        contract_signed: 'Lien envoyé',
        payment_sent: 'En attente de paiement',
        paid: 'Payée',
        cancelled: 'Annulée'
      };
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
          {labels[status] || status}
        </span>
      );
    };

    // Calculate discounted price
    const calculateDiscountedPrice = () => {
      if (!selectedPack) return { originalPrice: 0, discountedPrice: 0, discountAmount: 0 };
      
      const originalPrice = parseFloat(selectedPack.price) || 0;
      let discountAmount = 0;
      
      if (orderData.discount_value > 0) {
        if (orderData.discount_type === 'percentage') {
          discountAmount = originalPrice * (parseFloat(orderData.discount_value) / 100);
        } else {
          discountAmount = parseFloat(orderData.discount_value);
        }
      }
      
      const discountedPrice = Math.max(0, originalPrice - discountAmount);
      
      return {
        originalPrice,
        discountedPrice: Math.round(discountedPrice * 100) / 100,
        discountAmount: Math.round(discountAmount * 100) / 100
      };
    };

    const priceInfo = calculateDiscountedPrice();

    // Render New Order Form
    const renderNewOrderForm = () => (
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        {/* Progress Steps */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Client' },
              { num: 2, label: 'Pack' },
              { num: 3, label: 'Contrat' },
              { num: 4, label: 'Validation' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-[#DFFF00]' : 'text-slate-500'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    step >= s.num ? 'bg-[#DFFF00] text-black' : 'bg-slate-700 text-slate-400'
                  }`}>
                    {s.num}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > s.num ? 'bg-[#DFFF00]' : 'bg-slate-700'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Client Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Informations du client</h3>

                {/* Status selector: Client or Contact */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-400 mb-2">Statut</label>
                  <div className="flex gap-2" role="radiogroup">
                    {[
                      { id: 'client', label: 'Client', desc: 'A déjà loué ou paie' },
                      { id: 'contact', label: 'Contact (prospect)', desc: 'N\'a pas encore loué' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleInputChange('customer_type', opt.id)}
                        data-testid={`mo-customer-type-${opt.id}`}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                          orderData.customer_type === opt.id
                            ? 'border-[#DFFF00] bg-[#DFFF00]/10 text-white'
                            : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <div className="font-medium text-sm">{opt.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Prénom *</label>
                    <Input
                      value={orderData.client_first_name}
                      onChange={(e) => handleInputChange('client_first_name', e.target.value)}
                      placeholder="Prénom"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Nom *</label>
                    <Input
                      value={orderData.client_last_name}
                      onChange={(e) => handleInputChange('client_last_name', e.target.value)}
                      placeholder="Nom"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Email *</label>
                    <Input
                      type="email"
                      value={orderData.client_email}
                      onChange={(e) => handleInputChange('client_email', e.target.value)}
                      placeholder="email@exemple.com"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Téléphone</label>
                    <Input
                      type="tel"
                      value={orderData.client_phone}
                      onChange={(e) => handleInputChange('client_phone', e.target.value)}
                      placeholder="06 XX XX XX XX"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-400 mb-1">Société</label>
                    <Input
                      value={orderData.client_company}
                      onChange={(e) => handleInputChange('client_company', e.target.value)}
                      placeholder="Nom de l'entreprise"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-400 mb-1">Adresse</label>
                    <Input
                      value={orderData.client_address}
                      onChange={(e) => handleInputChange('client_address', e.target.value)}
                      placeholder="Adresse complète"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Code postal</label>
                    <Input
                      value={orderData.client_postal_code}
                      onChange={(e) => handleInputChange('client_postal_code', e.target.value)}
                      placeholder="75001"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Ville</label>
                    <Input
                      value={orderData.client_city}
                      onChange={(e) => handleInputChange('client_city', e.target.value)}
                      placeholder="Paris"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Informations vendeur</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Nom du vendeur *</label>
                  <select
                    value={orderData.seller_name}
                    onChange={(e) => handleInputChange('seller_name', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#DFFF00] focus:border-transparent"
                  >
                    <option value="">Sélectionner un vendeur</option>
                    <option value="Thierry FREDJ">Thierry FREDJ</option>
                    <option value="Axel FARRUGIA">Axel FARRUGIA</option>
                    <option value="Jules VEYRIE">Jules VEYRIE</option>
                    <option value="Thibault VOINIS">Thibault VOINIS</option>
                    <option value="Remi AGUADO">Remi AGUADO</option>
                    <option value="Benoit COVOLAN">Benoit COVOLAN</option>
                    <option value="Marwin HAREL">Marwin HAREL</option>
                    <option value="Stephane NONY">Stephane NONY</option>
                    <option value="Yves SANDLER">Yves SANDLER</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Pack Selection */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Sélection du pack</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {packOptions.map((pack) => (
                    <div
                      key={pack.id}
                      onClick={() => {
                        handleInputChange('selected_pack_id', pack.id);
                        // Reset custom pack fields when a catalogue pack is selected
                        handleInputChange('custom_pack_name', '');
                        handleInputChange('custom_pack_price', '');
                        handleInputChange('custom_pack_surface', '');
                      }}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        orderData.selected_pack_id === pack.id
                          ? 'border-[#DFFF00] bg-[#DFFF00]/10'
                          : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                      }`}
                    >
                      <h4 className="font-semibold text-white"><PackName name={pack.name} /></h4>
                      <p className="text-sm text-slate-400 mt-1">{pack.surface || pack.description}</p>
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-[#DFFF00] font-bold">{pack.price}€/mois</span>
                        <span className="text-xs text-slate-500">Caution: {pack.deposit}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Pack Option */}
              <div className="bg-slate-700/40 rounded-xl p-4 border border-slate-600">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <span className="text-[#DFFF00]">⚡</span>
                      Pack personnalisé
                    </h4>
                    <p className="text-xs text-slate-400 mt-1">
                      Configuration sur mesure — utilisez ces champs si aucun pack catalogue ne correspond.
                    </p>
                  </div>
                  {(orderData.custom_pack_name || orderData.custom_pack_price) && (
                    <button
                      onClick={() => {
                        handleInputChange('custom_pack_name', '');
                        handleInputChange('custom_pack_price', '');
                        handleInputChange('custom_pack_surface', '');
                      }}
                      className="text-xs text-slate-400 hover:text-white"
                      data-testid="reset-custom-pack-btn"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-400 mb-1">Nom du pack <span className="text-red-400">*</span></label>
                    <Input
                      type="text"
                      value={orderData.custom_pack_name}
                      onChange={(e) => {
                        handleInputChange('custom_pack_name', e.target.value);
                        if (e.target.value) handleInputChange('selected_pack_id', '');
                      }}
                      placeholder="Ex: Pack 20m² LED Outdoor"
                      className="bg-slate-800 border-slate-600 text-white"
                      data-testid="custom-pack-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Surface</label>
                    <Input
                      type="text"
                      value={orderData.custom_pack_surface}
                      onChange={(e) => handleInputChange('custom_pack_surface', e.target.value)}
                      placeholder="Ex: 20m²"
                      className="bg-slate-800 border-slate-600 text-white"
                      data-testid="custom-pack-surface-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Prix mensuel TTC (€) <span className="text-red-400">*</span></label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={orderData.custom_pack_price}
                      onChange={(e) => {
                        handleInputChange('custom_pack_price', e.target.value);
                        if (e.target.value) handleInputChange('selected_pack_id', '');
                      }}
                      placeholder="Ex: 4500"
                      className="bg-slate-800 border-slate-600 text-white"
                      data-testid="custom-pack-price-input"
                    />
                  </div>
                </div>
              </div>

              {/* Discount Section */}
              {selectedPack && (
                <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600">
                  <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Euro className="w-4 h-4 text-[#DFFF00]" />
                    Remise commerciale
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1">Type de remise</label>
                      <select
                        value={orderData.discount_type}
                        onChange={(e) => handleInputChange('discount_type', e.target.value)}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      >
                        <option value="percentage">Pourcentage (%)</option>
                        <option value="fixed">Montant fixe (€)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1">
                        Valeur {orderData.discount_type === 'percentage' ? '(%)' : '(€)'}
                      </label>
                      <Input
                        type="number"
                        min="0"
                        max={orderData.discount_type === 'percentage' ? 100 : selectedPack.price}
                        value={orderData.discount_value}
                        onChange={(e) => handleInputChange('discount_value', parseFloat(e.target.value) || 0)}
                        placeholder={orderData.discount_type === 'percentage' ? '10' : '50'}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1">Motif (optionnel)</label>
                      <Input
                        value={orderData.discount_reason}
                        onChange={(e) => handleInputChange('discount_reason', e.target.value)}
                        placeholder="Ex: Client fidèle"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  {/* Price Summary */}
                  {orderData.discount_value > 0 && (
                    <div className="mt-4 p-3 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-lg">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-400">Prix original :</span>
                        <span className="text-slate-300 line-through">{priceInfo.originalPrice}€/mois</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-slate-400">
                          Remise ({orderData.discount_type === 'percentage' ? `${orderData.discount_value}%` : `${orderData.discount_value}€`}) :
                        </span>
                        <span className="text-red-400">-{priceInfo.discountAmount}€</span>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-600">
                        <span className="font-semibold text-white">Prix final :</span>
                        <span className="font-bold text-[#DFFF00] text-lg">{priceInfo.discountedPrice}€/mois</span>
                      </div>
                    </div>
                  )}

                  {/* Custom caution + transport (override pack defaults) */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-600">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1">
                        Caution (€)
                        <span className="text-xs text-slate-500 ml-2">défaut : {selectedPack.deposit}€</span>
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={orderData.caution ?? ''}
                        onChange={(e) => handleInputChange('caution', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                        placeholder={`${selectedPack.deposit}`}
                        className="bg-slate-700 border-slate-600 text-white"
                        data-testid="manual-order-caution-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-1">
                        Frais de transport (€)
                        <span className="text-xs text-slate-500 ml-2">1<sup>er</sup> paiement uniquement</span>
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={orderData.transport_fees ?? 0}
                        onChange={(e) => handleInputChange('transport_fees', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="bg-slate-700 border-slate-600 text-white"
                        data-testid="manual-order-transport-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Date de début de location *</label>
                <Input
                  type="date"
                  value={orderData.rental_start_date}
                  onChange={(e) => handleInputChange('rental_start_date', e.target.value)}
                  min={new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="bg-slate-700 border-slate-600 text-white w-64"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Notes internes</label>
                <textarea
                  value={orderData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notes pour l'équipe..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
              </div>
            </div>
          )}

          {/* Step 3: Contract Info */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#DFFF00]" />
                  Envoi du lien de réservation
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Un lien de réservation personnalisé sera envoyé au client par email. Ce lien pré-remplira 
                  automatiquement toutes les informations saisies ci-dessus.
                </p>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-blue-400 text-sm">
                    <strong>Le client recevra un email avec :</strong><br />
                    1. Un récapitulatif de son offre (pack, prix, remise)<br />
                    2. Le contrat de location en pièce jointe<br />
                    3. Un bouton pour finaliser sa réservation en ligne<br />
                    <br />
                    <strong>En cliquant sur le lien, le client pourra :</strong><br />
                    - Vérifier ses informations pré-remplies<br />
                    - Signer son contrat<br />
                    - Procéder au paiement sécurisé
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Confirm */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white mb-4">Récapitulatif de la commande</h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-semibold text-[#DFFF00] mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" /> Client
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-white">{orderData.client_first_name} {orderData.client_last_name}</p>
                    <p className="text-slate-400">{orderData.client_email}</p>
                    <p className="text-slate-400">{orderData.client_phone}</p>
                    {orderData.client_company && <p className="text-slate-400">{orderData.client_company}</p>}
                  </div>
                </div>

                <div className="bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-semibold text-[#DFFF00] mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Pack sélectionné
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-white font-medium">{selectedPack?.name}</p>
                    <p className="text-slate-400">{selectedPack?.surface}</p>
                    {orderData.discount_value > 0 ? (
                      <>
                        <p className="text-slate-400 line-through">{selectedPack?.price}€/mois</p>
                        <div className="flex items-center gap-2">
                          <p className="text-[#DFFF00] font-bold">{priceInfo.discountedPrice}€/mois</p>
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                            -{orderData.discount_type === 'percentage' ? `${orderData.discount_value}%` : `${orderData.discount_value}€`}
                          </span>
                        </div>
                        {orderData.discount_reason && (
                          <p className="text-xs text-slate-500">Motif: {orderData.discount_reason}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-[#DFFF00] font-bold">{selectedPack?.price}€/mois</p>
                    )}
                    <p className="text-slate-500">Caution: {selectedPack?.deposit}€</p>
                  </div>
                </div>

                <div className="bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-semibold text-[#DFFF00] mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> Date de début
                  </h4>
                  <p className="text-white">{new Date(orderData.rental_start_date).toLocaleDateString('fr-FR', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}</p>
                </div>

                <div className="bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-semibold text-[#DFFF00] mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" /> Vendeur
                  </h4>
                  <p className="text-white">{orderData.seller_name}</p>
                </div>
              </div>

              {orderData.notes && (
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-300 mb-2">Notes</h4>
                  <p className="text-slate-400 text-sm">{orderData.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-slate-700">
            <Button
              variant="outline"
              onClick={() => step === 1 ? setShowNewOrder(false) : prevStep()}
              className="border-slate-600 text-slate-300"
            >
              {step === 1 ? 'Annuler' : 'Précédent'}
            </Button>
            
            {step < 4 ? (
              <Button onClick={nextStep} className="bg-[#DFFF00] hover:bg-[#c8e600] text-black">
                Suivant
              </Button>
            ) : (
              <Button 
                onClick={createManualOrder} 
                disabled={creating}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
              >
                {creating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Créer la commande
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );

    // Render Edit Modal
    const renderEditModal = () => {
      if (!editingOrder) return null;
      const canResend = !['shipped', 'delivered', 'active', 'completed', 'paid'].includes(editingOrder.status);
      return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditingOrder(null)}>
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Pencil className="w-5 h-5 text-[#DFFF00]" />
                Modifier la commande
              </h3>
              <button onClick={() => setEditingOrder(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div>
                <h4 className="text-sm font-semibold text-[#DFFF00] uppercase tracking-wider mb-3">Informations client</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Prénom *</label>
                    <Input value={editData.client_first_name} onChange={e => handleEditChange('client_first_name', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-first-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Nom *</label>
                    <Input value={editData.client_last_name} onChange={e => handleEditChange('client_last_name', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-last-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Email *</label>
                    <Input type="email" value={editData.client_email} onChange={e => handleEditChange('client_email', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Téléphone</label>
                    <Input type="tel" value={editData.client_phone} onChange={e => handleEditChange('client_phone', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-phone" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-400 mb-1">Société</label>
                    <Input value={editData.client_company} onChange={e => handleEditChange('client_company', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-company" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-400 mb-1">Adresse</label>
                    <Input value={editData.client_address} onChange={e => handleEditChange('client_address', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-address" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Code postal</label>
                    <Input value={editData.client_postal_code} onChange={e => handleEditChange('client_postal_code', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-postal-code" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-1">Ville</label>
                    <Input value={editData.client_city} onChange={e => handleEditChange('client_city', e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="edit-client-city" />
                  </div>
                </div>
              </div>

              {/* Seller */}
              <div>
                <h4 className="text-sm font-semibold text-[#DFFF00] uppercase tracking-wider mb-3">Vendeur</h4>
                <select
                  value={editData.seller_name}
                  onChange={e => handleEditChange('seller_name', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#DFFF00]"
                  data-testid="edit-seller-name"
                >
                  <option value="">Sélectionner un vendeur</option>
                  <option value="Thierry FREDJ">Thierry FREDJ</option>
                  <option value="Axel FARRUGIA">Axel FARRUGIA</option>
                  <option value="Jules VEYRIE">Jules VEYRIE</option>
                  <option value="Thibault VOINIS">Thibault VOINIS</option>
                  <option value="Remi AGUADO">Remi AGUADO</option>
                  <option value="Benoit COVOLAN">Benoit COVOLAN</option>
                  <option value="Marwin HAREL">Marwin HAREL</option>
                  <option value="Stephane NONY">Stephane NONY</option>
                  <option value="Yves SANDLER">Yves SANDLER</option>
                </select>
              </div>

              {/* Pack Selection */}
              <div>
                <h4 className="text-sm font-semibold text-[#DFFF00] uppercase tracking-wider mb-3">Pack & Tarification</h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {packOptions.map(pack => (
                    <div
                      key={pack.id}
                      onClick={() => handleEditChange('selected_pack_id', pack.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                        editData.selected_pack_id === pack.id
                          ? 'border-[#DFFF00] bg-[#DFFF00]/10'
                          : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                      }`}
                      data-testid={`edit-pack-${pack.id}`}
                    >
                      <p className="font-semibold text-white"><PackName name={pack.name} /></p>
                      <p className="text-[#DFFF00] font-bold mt-1">{pack.price}€/mois</p>
                    </div>
                  ))}
                </div>

                {/* Discount */}
                {editSelectedPack && (
                  <div className="mt-4 bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Type remise</label>
                        <select
                          value={editData.discount_type}
                          onChange={e => handleEditChange('discount_type', e.target.value)}
                          className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                          data-testid="edit-discount-type"
                        >
                          <option value="percentage">% Pourcentage</option>
                          <option value="fixed">€ Montant fixe</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Valeur</label>
                        <Input
                          type="number" min="0"
                          value={editData.discount_value}
                          onChange={e => handleEditChange('discount_value', parseFloat(e.target.value) || 0)}
                          className="bg-slate-700 border-slate-600 text-white text-sm"
                          data-testid="edit-discount-value"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Motif</label>
                        <Input
                          value={editData.discount_reason}
                          onChange={e => handleEditChange('discount_reason', e.target.value)}
                          className="bg-slate-700 border-slate-600 text-white text-sm"
                          data-testid="edit-discount-reason"
                        />
                      </div>
                    </div>
                    {editData.discount_value > 0 && (
                      <div className="mt-3 p-2 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded flex justify-between items-center text-sm">
                        <span className="text-slate-400">Prix original: <span className="line-through">{editPriceInfo.originalPrice}€</span></span>
                        <span className="text-[#DFFF00] font-bold">{editPriceInfo.discountedPrice}€/mois</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Date & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Date de début de location</label>
                  <Input
                    type="date"
                    value={editData.rental_start_date}
                    onChange={e => handleEditChange('rental_start_date', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="edit-rental-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Notes internes</label>
                  <Input
                    value={editData.notes}
                    onChange={e => handleEditChange('notes', e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="edit-notes"
                  />
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between p-6 border-t border-slate-700">
              <Button variant="outline" onClick={() => setEditingOrder(null)} className="border-slate-600 text-slate-300">
                Annuler
              </Button>
              <div className="flex gap-2">
                {canResend && (
                  <Button
                    onClick={async () => { await saveEdit(); if (editingOrder) resendLink(editingOrder.id); }}
                    disabled={saving || sendingLink}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="edit-save-and-resend"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder & Renvoyer le lien'}
                  </Button>
                )}
                <Button
                  onClick={saveEdit}
                  disabled={saving}
                  className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                  data-testid="edit-save-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {renderEditModal()}
        {/* Delete confirmation modal */}
        {deletingOrder && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { setDeletingOrder(null); setDeletePassword(''); }}>
            <div className="bg-slate-800 rounded-xl border border-red-500/50 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="p-6 space-y-4">
                <h3 className="text-lg font-bold text-red-400 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" /> Supprimer la commande
                </h3>
                <p className="text-slate-300 text-sm">
                  Supprimer la commande de <strong>{deletingOrder.client_first_name} {deletingOrder.client_last_name}</strong> ({deletingOrder.pack_name}) ?
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">Mot de passe logistique</label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder="Mot de passe"
                    className="bg-slate-700 border-slate-600 text-white"
                    data-testid="delete-password-input"
                    onKeyDown={e => e.key === 'Enter' && confirmDelete()}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setDeletingOrder(null); setDeletePassword(''); }} className="border-slate-600 text-slate-300">
                    Annuler
                  </Button>
                  <Button onClick={confirmDelete} disabled={deleting || !deletePassword} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Commandes manuelles</h2>
            <p className="text-slate-400 text-sm">Créez des commandes et envoyez le lien de réservation à vos clients</p>
          </div>
          {!showNewOrder && (
            <Button 
              onClick={() => setShowNewOrder(true)}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle commande
            </Button>
          )}
        </div>

        {showNewOrder ? (
          renderNewOrderForm()
        ) : (
          <>
            {/* Manual Orders List */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-4 text-slate-400 font-medium text-sm">Client</th>
                      <th className="text-left p-4 text-slate-400 font-medium text-sm">Pack</th>
                      <th className="text-left p-4 text-slate-400 font-medium text-sm">Vendeur</th>
                      <th className="text-left p-4 text-slate-400 font-medium text-sm">Statut</th>
                      <th className="text-left p-4 text-slate-400 font-medium text-sm">Date</th>
                      <th className="text-right p-4 text-slate-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingOrders ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                          Chargement...
                        </td>
                      </tr>
                    ) : manualOrders.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          Aucune commande manuelle
                        </td>
                      </tr>
                    ) : (
                      manualOrders.map((order) => (
                        <tr key={order.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div>
                              <p className="text-white font-medium">
                                {order.client_first_name || order.client?.firstName || ''} {order.client_last_name || order.client?.lastName || ''}
                              </p>
                              <p className="text-slate-400 text-sm">{order.client_email || order.client?.email || ''}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <p className="text-slate-300"><PackName name={order.pack_name} /></p>
                              {order.discount_value > 0 ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[#DFFF00] font-semibold text-sm">{order.final_price}€</span>
                                  <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">
                                    -{order.discount_type === 'percentage' ? `${order.discount_value}%` : `${order.discount_value}€`}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-500 text-sm">{order.pack_price || order.final_price || order.original_price || ''}€/mois</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-slate-300">{order.seller_name || order.commercial_name || ''}</td>
                          <td className="p-4">
                            {getStatusBadge(order.status)}
                            {order.converted_reservation_id && (
                              <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`linked-order-${order.id}`}>
                                <PackageCheck className="w-3 h-3" /> Commande créée
                              </div>
                            )}
                          </td>
                          <td className="p-4 text-slate-400 text-sm">
                            {new Date(order.created_at).toLocaleDateString('fr-FR')}
                            {order.link_sent_at && (
                              <div className="text-xs text-blue-400 mt-1">
                                Lien envoyé le {new Date(order.link_sent_at).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex justify-end gap-2 flex-wrap">
                              {/* Modifier le statut — toujours visible */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMOStatusEditor(order)}
                                className="border-slate-500 text-slate-200 hover:bg-slate-700 text-xs"
                                data-testid={`edit-mo-status-${order.id}`}
                              >
                                <Building2 className="w-3 h-3 mr-1" />
                                Statut
                              </Button>
                              {/* Modifier - visible tant que non expédié */}
                              {!BLOCKED_EDIT_STATUSES.includes(order.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditModal(order)}
                                  className="border-[#DFFF00]/50 text-[#DFFF00] hover:bg-[#DFFF00]/10 text-xs"
                                  data-testid={`edit-order-${order.id}`}
                                >
                                  <Pencil className="w-3 h-3 mr-1" />
                                  Modifier
                                </Button>
                              )}

                              {/* Aperçu du contrat - toujours visible */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => previewContract(order.id)}
                                className="border-slate-600 text-slate-300 hover:text-white text-xs"
                                data-testid={`preview-contract-${order.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Contrat
                              </Button>
                              
                              {/* Envoyer le lien par mail - visible en draft */}
                              {order.status === 'draft' && (
                                <Button
                                  size="sm"
                                  onClick={() => sendReservationLink(order.id)}
                                  disabled={sendingLink}
                                  className="bg-[#DFFF00] hover:bg-[#c8e600] text-black text-xs font-semibold"
                                  data-testid={`send-link-${order.id}`}
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  {sendingLink ? 'Envoi...' : 'Envoyer le lien'}
                                </Button>
                              )}
                              
                              {/* Renvoyer le lien - visible pour link_sent, contract_sent, contract_signed, pending_validation */}
                              {['link_sent', 'contract_sent', 'contract_signed', 'pending_validation'].includes(order.status) && (
                                <Button
                                  size="sm"
                                  onClick={() => resendLink(order.id)}
                                  disabled={sendingLink}
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                  data-testid={`resend-link-${order.id}`}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  {sendingLink ? 'Envoi...' : 'Renvoyer le lien'}
                                </Button>
                              )}

                              {/* Voir le lien - visible quand le lien existe */}
                              {order.reservation_link && !['draft', 'cancelled'].includes(order.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(order.reservation_link);
                                    toast.success('Lien copié dans le presse-papier !');
                                  }}
                                  className="border-slate-600 text-slate-300 hover:text-white text-xs"
                                  data-testid={`copy-link-${order.id}`}
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Copier le lien
                                </Button>
                              )}

                              {/* Demander un prélèvement SEPA — visible si pas encore payé et pas de mandat actif */}
                              {!['paid', 'cancelled'].includes(order.status) && !order.converted_reservation_id && !order.stripe_subscription_id && (
                                <Button
                                  size="sm"
                                  onClick={() => requestSEPAMandate(order.id)}
                                  disabled={sendingLink}
                                  className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/40 text-xs font-semibold"
                                  data-testid={`request-sepa-${order.id}`}
                                  title={order.sepa_request_id
                                    ? "Une demande SEPA existe déjà — annulez-la d'abord pour en relancer une"
                                    : "Envoie au client un lien direct pour signer son mandat SEPA"}
                                >
                                  <Building2 className="w-3 h-3 mr-1" />
                                  {order.sepa_request_id ? 'Demande SEPA envoyée' : 'Demander prélèvement SEPA'}
                                </Button>
                              )}

                              {/* Créer la commande à préparer — commande payée hors ligne (virement) sans réservation liée */}
                              {order.status === 'paid' && !order.converted_reservation_id && (
                                <Button
                                  size="sm"
                                  onClick={() => createReservationFromOrder(order.id)}
                                  disabled={sendingLink}
                                  className="bg-[#DFFF00] hover:bg-[#c8e600] text-black text-xs font-semibold"
                                  data-testid={`create-reservation-${order.id}`}
                                  title="Créer la commande à préparer (paiement par virement encaissé)"
                                >
                                  <PackageCheck className="w-3 h-3 mr-1" />
                                  {sendingLink ? 'Création...' : 'Créer la commande à préparer'}
                                </Button>
                              )}

                              {/* Voir la RC pro — visible quand la commande est payée et qu'une RC a été uploadée */}
                              {order.status === 'paid' && order.has_rc_pro && order.converted_reservation_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(`${API}/admin/orders/${order.converted_reservation_id}/rc-pro`, '_blank')}
                                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                                  data-testid={`view-rc-pro-${order.id}`}
                                  title="Ouvrir la RC professionnelle"
                                >
                                  <Shield className="w-3 h-3 mr-1" />
                                  RC pro
                                </Button>
                              )}

                              {/* Supprimer */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeletingOrder(order)}
                                className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                                data-testid={`delete-order-${order.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Status Editor Modal (manual orders) */}
        {statusEditMO && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="mo-status-edit-modal">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-white mb-1">Modifier le statut</h3>
              <p className="text-sm text-slate-400 mb-5 truncate">
                {statusEditMO.client_first_name} {statusEditMO.client_last_name} · {statusEditMO.client_company || statusEditMO.client_email}
              </p>

              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Statut de la commande / paiement</label>
              <select
                value={moNewStatus}
                onChange={(e) => setMoNewStatus(e.target.value)}
                className="w-full mb-6 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#DFFF00]"
                data-testid="mo-status-select"
              >
                <option value="draft">Brouillon</option>
                <option value="link_sent">Lien envoyé</option>
                <option value="contract_sent">Contrat envoyé</option>
                <option value="contract_signed">Contrat signé</option>
                <option value="payment_sent">En attente de paiement</option>
                <option value="paid">Payée</option>
                <option value="cancelled">Annulée</option>
              </select>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => setStatusEditMO(null)}
                  disabled={moStatusSaving}
                  data-testid="mo-status-cancel"
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
                  onClick={saveMOStatus}
                  disabled={moStatusSaving}
                  data-testid="mo-status-save"
                >
                  {moStatusSaving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
};

export default ManualOrdersView;
